import type { CrestBrowserLaunchConfig } from './crest-auth-config.js';

export interface CrestAuthSession {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
  readonly userId: string;
}

export type CrestAuthRestoreResult =
  | { readonly status: 'unauthenticated'; readonly recoveryAttempted: boolean }
  | { readonly status: 'authenticated'; readonly session: CrestAuthSession }
  | { readonly status: 'recovery'; readonly userId: string; readonly expiresAt: number };

export interface CrestSessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const crestAuthSessionStorageKey = 'crest.crm2.auth.session.v1';
const refreshAheadMs = 60_000;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function uuidValue(value: unknown): string | null {
  const string = stringValue(value);
  return string !== null && uuidPattern.test(string) ? string.toLowerCase() : null;
}

function positiveNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function parseSession(value: unknown): CrestAuthSession | null {
  if (!isRecord(value)) return null;
  const accessToken = stringValue(value.accessToken);
  const refreshToken = stringValue(value.refreshToken);
  const expiresAt = positiveNumber(value.expiresAt);
  const userId = uuidValue(value.userId);
  return accessToken === null || refreshToken === null || expiresAt === null || userId === null
    ? null
    : { accessToken, refreshToken, expiresAt, userId };
}

class InMemorySessionStorage implements CrestSessionStorage {
  private readonly values = new Map<string, string>();

  public getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  public setItem(key: string, value: string): void {
    this.values.set(key, value);
  }

  public removeItem(key: string): void {
    this.values.delete(key);
  }
}

class SafeSessionStorage implements CrestSessionStorage {
  private readonly fallback = new InMemorySessionStorage();

  public getItem(key: string): string | null {
    try {
      return globalThis.sessionStorage.getItem(key);
    } catch {
      return this.fallback.getItem(key);
    }
  }

  public setItem(key: string, value: string): void {
    try {
      globalThis.sessionStorage.setItem(key, value);
    } catch {
      this.fallback.setItem(key, value);
    }
  }

  public removeItem(key: string): void {
    try {
      globalThis.sessionStorage.removeItem(key);
    } catch {
      this.fallback.removeItem(key);
    }
  }
}

function browserSessionStorage(): CrestSessionStorage {
  return new SafeSessionStorage();
}

export type CrestAuthFailureStage =
  | 'session-missing'
  | 'session-changed'
  | 'password-change-invalid'
  | 'password-change-busy'
  | 'password-change-session-changed'
  | 'password-change-user'
  | 'password-change-reauthentication'
  | 'password-change-rejected'
  | 'password-change-unconfirmed'
  | 'magic-link-response'
  | 'password-token-request'
  | 'password-token-response'
  | 'recovery-redirect'
  | 'recovery-request'
  | 'recovery-session-missing'
  | 'recovery-session-expired'
  | 'recovery-password-request'
  | 'recovery-password-response'
  | 'refresh-token-request'
  | 'refresh-token-response'
  | 'user-request'
  | 'user-response';

export class CrestAuthFailure extends Error {
  public override readonly name = 'CrestAuthFailure';

  public constructor(
    public readonly stage: CrestAuthFailureStage,
    public readonly status: number | null = null,
  ) {
    super('Authentication could not be completed.');
  }
}

function authFailure(stage: CrestAuthFailureStage, status: number | null = null): CrestAuthFailure {
  return new CrestAuthFailure(stage, status);
}

export function crestAuthFailureDiagnostic(error: unknown): string {
  if (!(error instanceof CrestAuthFailure)) return 'crest-auth:unexpected-failure';
  return error.status === null
    ? `crest-auth:${error.stage}`
    : `crest-auth:${error.stage}:http-${error.status}`;
}

export interface CrestSignedInPasswordScope {
  readonly tenantId: string;
  readonly userId: string;
  readonly isCurrent: () => boolean;
}

export type CrestPasswordChangeOutcome = 'not-submitted' | 'rejected' | 'unconfirmed' | 'changed';

export class CrestPasswordChangeFailure extends CrestAuthFailure {
  public constructor(
    stage: CrestAuthFailureStage,
    public readonly outcome: CrestPasswordChangeOutcome,
    public readonly temporarySessionClosed: boolean,
    status: number | null = null,
  ) {
    super(stage, status);
  }
}

interface CrestAuthMarker {
  readonly generation: number;
  readonly storedValue: string | null;
}

export class CrestAuthClient {
  private session: CrestAuthSession | null = null;
  private recoverySession: CrestAuthSession | null = null;
  private restorePromise: Promise<CrestAuthRestoreResult> | null = null;
  private refreshPromise: { readonly generation: number; readonly promise: Promise<CrestAuthSession> } | null = null;
  private generation = 0;
  private passwordChangePending = false;

  public constructor(
    private readonly config: CrestBrowserLaunchConfig,
    private readonly storage: CrestSessionStorage = browserSessionStorage(),
    private readonly fetcher: typeof fetch = fetch,
    private readonly now: () => number = Date.now,
  ) {}

  public async restore(): Promise<CrestAuthRestoreResult> {
    if (this.restorePromise !== null) return this.restorePromise;
    this.restorePromise = this.restoreSession().finally(() => {
      this.restorePromise = null;
    });
    return this.restorePromise;
  }

  private async restoreSession(): Promise<CrestAuthRestoreResult> {
    const marker = this.captureMarker();
    const callback = this.readAuthCallback();
    if (callback !== null) {
      this.clearAuthCallback(callback.kind === 'recovery' || callback.recoveryAttempted);
      if (callback.session === null) {
        this.clearAllSessions();
        return { status: 'unauthenticated', recoveryAttempted: callback.recoveryAttempted };
      }
      try {
        const userId = await this.readCurrentUserId(callback.session.accessToken);
        this.assertMarker(marker);
        const session = { ...callback.session, userId };
        if (callback.kind === 'recovery') {
          this.clearSession();
          this.recoverySession = session;
          return { status: 'recovery', userId, expiresAt: session.expiresAt };
        }
        this.persistSession(session);
        return { status: 'authenticated', session };
      } catch {
        if (this.isMarkerCurrent(marker)) this.clearAllSessions();
        return { status: 'unauthenticated', recoveryAttempted: callback.recoveryAttempted };
      }
    }
    const rawSession = this.storage.getItem(crestAuthSessionStorageKey);
    if (rawSession === null) return { status: 'unauthenticated', recoveryAttempted: false };
    const parsed = parseSession(this.parseStoredSession(rawSession));
    if (parsed === null) {
      this.clearSession();
      return { status: 'unauthenticated', recoveryAttempted: false };
    }
    this.session = parsed;
    try {
      const current = await this.currentSession();
      const currentMarker = this.captureMarker();
      await this.validateCurrentUser(current);
      this.assertMarker(currentMarker);
      if (this.generation !== marker.generation) throw authFailure('session-changed');
      return { status: 'authenticated', session: current };
    } catch {
      if (this.isMarkerCurrent(marker)) this.clearSession();
      return { status: 'unauthenticated', recoveryAttempted: false };
    }
  }

  public async requestPasswordRecovery(email: string): Promise<void> {
    const redirectUrl = this.recoveryRedirectUrl();
    let response: Response;
    try {
      const fetcher = this.fetcher;
      response = await fetcher(
        `${this.config.supabaseUrl}/auth/v1/recover?redirect_to=${encodeURIComponent(redirectUrl)}`,
        {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({ email: email.trim() }),
          credentials: 'omit',
          cache: 'no-store',
        },
      );
    } catch {
      throw authFailure('recovery-request');
    }
    // Supabase intentionally returns a neutral response for existing and
    // absent accounts. Do not expose response status or body in the UI because
    // provider rate-limit differences can also become an enumeration signal.
    await response.body?.cancel().catch(() => undefined);
  }

  public async updateRecoveredPassword(password: string): Promise<void> {
    const recovery = this.recoverySession;
    if (recovery === null) throw authFailure('recovery-session-missing');
    if (recovery.expiresAt <= this.now()) {
      this.clearAllSessions();
      throw authFailure('recovery-session-expired');
    }
    let response: Response;
    try {
      const fetcher = this.fetcher;
      response = await fetcher(`${this.config.supabaseUrl}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          ...this.authHeaders(),
          authorization: `Bearer ${recovery.accessToken}`,
        },
        body: JSON.stringify({ password }),
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      throw authFailure('recovery-password-request');
    }
    const body: unknown = await response.json().catch(() => null);
    const updatedUserId = isRecord(body) ? uuidValue(body.id) : null;
    if (!response.ok || updatedUserId !== recovery.userId) {
      throw authFailure('recovery-password-response', response.status);
    }

    this.clearAllSessions();
    this.replaceBrowserUrl('/');
    try {
      const fetcher = this.fetcher;
      await fetcher(`${this.config.supabaseUrl}/auth/v1/logout?scope=global`, {
        method: 'POST',
        headers: {
          ...this.authHeaders(),
          authorization: `Bearer ${recovery.accessToken}`,
        },
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      // A supported password update revokes other provider sessions. The
      // explicit global logout is defense in depth; local recovery authority
      // remains cleared even when that follow-up endpoint is unavailable.
    }
  }

  public cancelPasswordRecovery(): void {
    this.clearAllSessions();
    this.replaceBrowserUrl('/');
  }

  public async signInWithPassword(email: string, password: string): Promise<CrestAuthSession> {
    this.generation += 1;
    const marker = this.captureMarker();
    let response: Response;
    try {
      const fetcher = this.fetcher;
      response = await fetcher(`${this.config.supabaseUrl}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ email, password }),
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      throw authFailure('password-token-request');
    }
    const session = await this.readAuthResponse(response, 'password-token-response');
    await this.validateCurrentUser(session);
    this.assertMarker(marker);
    this.recoverySession = null;
    this.persistSession(session);
    return session;
  }

  public async getAccessToken(): Promise<string | null> {
    if (this.session === null) return null;
    const marker = this.captureMarker();
    try {
      return (await this.currentSession()).accessToken;
    } catch {
      if (this.isMarkerCurrent(marker)) this.clearSession();
      return null;
    }
  }

  public async signOut(): Promise<void> {
    const current = this.session;
    this.clearSession();
    if (current === null) return;
    try {
      const fetcher = this.fetcher;
      await fetcher(`${this.config.supabaseUrl}/auth/v1/logout`, {
        method: 'POST',
        headers: {
          ...this.authHeaders(),
          authorization: `Bearer ${current.accessToken}`,
        },
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      // Local session clearance is the safe sign-out outcome when the Auth service is unavailable.
    }
  }

  private async currentSession(): Promise<CrestAuthSession> {
    if (this.session === null) throw authFailure('session-missing');
    if (this.session.expiresAt - this.now() > refreshAheadMs) return this.session;
    return this.refreshSession(this.session);
  }

  private async refreshSession(source: CrestAuthSession): Promise<CrestAuthSession> {
    if (this.refreshPromise?.generation === this.generation) return this.refreshPromise.promise;
    const generation = this.generation;
    const promise = this.rotateRefreshToken(source).finally(() => {
      if (this.refreshPromise?.promise === promise) this.refreshPromise = null;
    });
    this.refreshPromise = { generation, promise };
    return promise;
  }

  private async rotateRefreshToken(source: CrestAuthSession): Promise<CrestAuthSession> {
    const marker = this.captureMarker();
    let response: Response;
    try {
      const fetcher = this.fetcher;
      response = await fetcher(`${this.config.supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ refresh_token: source.refreshToken }),
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      throw authFailure('refresh-token-request');
    }
    const session = await this.readAuthResponse(response, 'refresh-token-response');
    if (session.userId !== source.userId) throw authFailure('user-response');
    await this.validateCurrentUser(session);
    this.assertMarker(marker);
    this.persistSession(session);
    return session;
  }

  private async validateCurrentUser(session: CrestAuthSession): Promise<void> {
    const userId = await this.readCurrentUserId(session.accessToken);
    if (userId !== session.userId) throw authFailure('user-response');
  }

  private async readCurrentUserId(accessToken: string): Promise<string> {
    let response: Response;
    try {
      const fetcher = this.fetcher;
      response = await fetcher(`${this.config.supabaseUrl}/auth/v1/user`, {
        method: 'GET',
        headers: {
          apikey: this.config.supabasePublishableKey,
          authorization: `Bearer ${accessToken}`,
        },
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      throw authFailure('user-request');
    }
    const body: unknown = await response.json().catch(() => null);
    const userId = isRecord(body) ? uuidValue(body.id) : null;
    if (!response.ok || userId === null) {
      throw authFailure('user-response', response.status);
    }
    return userId;
  }

  private readAuthCallback(): {
    readonly kind: 'magic-link' | 'recovery';
    readonly recoveryAttempted: boolean;
    readonly session: Omit<CrestAuthSession, 'userId'> | null;
  } | null {
    const location = globalThis.location;
    if (location === undefined) return null;
    const recoveryPath = location.pathname === '/reset-password';
    if (!location.hash.startsWith('#')) {
      return recoveryPath
        ? { kind: 'recovery', recoveryAttempted: true, session: null }
        : null;
    }
    const values = new URLSearchParams(location.hash.slice(1));
    const callbackType = values.get('type')?.toLowerCase() ?? null;
    const recoveryAttempted = recoveryPath || callbackType === 'recovery';
    const kind = callbackType === 'recovery' ? 'recovery' : 'magic-link';
    const accessToken = stringValue(values.get('access_token'));
    const refreshToken = stringValue(values.get('refresh_token'));
    if (accessToken === null || refreshToken === null || (recoveryPath && kind !== 'recovery')) {
      return { kind, recoveryAttempted, session: null };
    }
    const tokenType = values.get('token_type');
    if (tokenType !== null && tokenType.toLowerCase() !== 'bearer') {
      return { kind, recoveryAttempted, session: null };
    }
    const expiresAtSeconds = Number(values.get('expires_at'));
    const expiresInSeconds = Number(values.get('expires_in'));
    const expiresAt = Number.isFinite(expiresAtSeconds) && expiresAtSeconds > 0
      ? expiresAtSeconds * 1_000
      : Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
        ? this.now() + expiresInSeconds * 1_000
        : null;
    if (expiresAt === null || expiresAt <= this.now()) {
      return { kind, recoveryAttempted, session: null };
    }
    return { kind, recoveryAttempted, session: { accessToken, refreshToken, expiresAt } };
  }

  private clearAuthCallback(recoveryAttempted: boolean): void {
    const location = globalThis.location;
    if (location === undefined || location.hash === '') return;
    this.replaceBrowserUrl(recoveryAttempted ? '/reset-password' : `${location.pathname}${location.search}`);
  }

  private recoveryRedirectUrl(): string {
    const location = globalThis.location;
    if (location === undefined) throw authFailure('recovery-redirect');
    let origin: URL;
    try {
      origin = new URL(location.origin);
    } catch {
      throw authFailure('recovery-redirect');
    }
    const allowsHttpLoopback = origin.protocol === 'http:' &&
      (origin.hostname === 'localhost' || origin.hostname === '127.0.0.1' || origin.hostname === '::1');
    if ((origin.protocol !== 'https:' && !allowsHttpLoopback) || origin.username !== '' || origin.password !== '' ||
        origin.pathname !== '/' || origin.search !== '' || origin.hash !== '') {
      throw authFailure('recovery-redirect');
    }
    return new URL('/reset-password', origin).toString();
  }

  private replaceBrowserUrl(path: string): void {
    const history = globalThis.history;
    if (history === undefined) return;
    history.replaceState(history.state, '', path);
  }

  private async readAuthResponse(
    response: Response,
    stage: 'password-token-response' | 'refresh-token-response' | 'password-change-reauthentication',
    parsedBody?: unknown,
  ): Promise<CrestAuthSession> {
    const body: unknown = parsedBody === undefined ? await response.json().catch(() => null) : parsedBody;
    if (!response.ok || !isRecord(body) || !isRecord(body.user)) {
      throw authFailure(stage, response.status);
    }
    const accessToken = stringValue(body.access_token);
    const refreshToken = stringValue(body.refresh_token);
    const expiresIn = positiveNumber(body.expires_in);
    const expiresAt = body.expires_at === undefined ? null : positiveNumber(body.expires_at);
    const userId = uuidValue(body.user.id);
    if (accessToken === null || refreshToken === null || expiresIn === null || userId === null) {
      throw authFailure(stage, response.status);
    }
    return {
      accessToken,
      refreshToken,
      expiresAt: (expiresAt ?? Math.floor(this.now() / 1_000) + expiresIn) * 1_000,
      userId,
    };
  }

  private authHeaders(): Record<string, string> {
    return {
      apikey: this.config.supabasePublishableKey,
      'content-type': 'application/json',
      accept: 'application/json',
    };
  }

  private persistSession(session: CrestAuthSession): void {
    this.session = session;
    this.storage.setItem(crestAuthSessionStorageKey, JSON.stringify(session));
  }

  private clearSession(): void {
    this.generation += 1;
    this.session = null;
    this.storage.removeItem(crestAuthSessionStorageKey);
  }

  private clearAllSessions(): void {
    this.clearSession();
    this.recoverySession = null;
  }


  private captureMarker(): CrestAuthMarker {
    return { generation: this.generation, storedValue: this.storage.getItem(crestAuthSessionStorageKey) };
  }

  private isMarkerCurrent(marker: CrestAuthMarker): boolean {
    return this.generation === marker.generation &&
      this.storage.getItem(crestAuthSessionStorageKey) === marker.storedValue;
  }

  private assertMarker(marker: CrestAuthMarker): void {
    if (!this.isMarkerCurrent(marker)) throw authFailure('session-changed');
  }

  private passwordScopeIsCurrent(scope: CrestSignedInPasswordScope): boolean {
    try {
      return scope.tenantId === this.config.tenantId &&
        this.session?.userId === scope.userId && this.recoverySession === null && scope.isCurrent();
    } catch {
      return false;
    }
  }

  private passwordOriginIsCurrent(scope: CrestSignedInPasswordScope, generation: number): boolean {
    // A refresh of this login changes token bytes, not its identity generation.
    // External storage replacement and every new login/sign-out still invalidate it.
    return this.generation === generation && this.session !== null &&
      this.session.userId === scope.userId && scope.tenantId === this.config.tenantId &&
      this.recoverySession === null &&
      this.storage.getItem(crestAuthSessionStorageKey) === JSON.stringify(this.session);
  }

  // Bound headers and body consumption together; never retry a credential mutation.
  // A timed-out PUT is unconfirmed even if its headers reported success.
  private async passwordRequest(path: '/user' | '/token?grant_type=password' | '/logout?scope=local',
    init: RequestInit): Promise<Readonly<{ response: Response; body: unknown }>> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        reject(new Error('Password request timed out.'));
        controller.abort();
      }, 30_000);
    });
    try {
      const fetcher = this.fetcher;
      const consumed = (async () => {
        const response = await fetcher(this.config.supabaseUrl + '/auth/v1' + path, {
          ...init, signal: controller.signal, credentials: 'omit', cache: 'no-store',
        });
        if (path === '/logout?scope=local') {
          await response.body?.cancel().catch(() => undefined);
          return { response, body: null };
        }
        const body: unknown = await response.json().catch(() => null);
        return { response, body };
      })();
      // AbortSignal is advisory to injected transports. Late body completion must
      // not resume the caller or mutate auth state after the deadline wins.
      return await Promise.race([consumed, timeout]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Normal self-service only. Reauthentication creates an unpersisted normal
   * provider session; it never repurposes the recovery session or an admin port.
   * The caller supplies a live UI-context fence, not authority over another user.
   */
  public async changeSignedInPassword(
    input: Readonly<{ currentPassword: string; newPassword: string }>,
    scope: CrestSignedInPasswordScope,
  ): Promise<Readonly<{ temporarySessionClosed: boolean }>> {
    if (typeof input.currentPassword !== 'string' || input.currentPassword.length === 0 ||
        typeof input.newPassword !== 'string' || input.newPassword.length < 8 ||
        input.newPassword.length > 128) throw authFailure('password-change-invalid');
    if (this.passwordChangePending) throw authFailure('password-change-busy');
    if (!this.passwordScopeIsCurrent(scope)) throw authFailure('password-change-session-changed');

    this.passwordChangePending = true;
    const startingGeneration = this.generation;
    let temporary: CrestAuthSession | null = null;
    let temporaryAccessToken: string | null = null;
    let outcome: CrestPasswordChangeOutcome = 'not-submitted';
    let failure: CrestAuthFailure | null = null;
    let temporarySessionClosed = true;
    const assertCurrent = () => {
      if (!this.passwordOriginIsCurrent(scope, startingGeneration) || !this.passwordScopeIsCurrent(scope)) {
        throw authFailure('password-change-session-changed');
      }
    };
    try {
      const current = await this.currentSession();
      assertCurrent();
      const { response: userResponse, body: user } = await this.passwordRequest('/user', {
        method: 'GET',
        headers: { ...this.authHeaders(), authorization: 'Bearer ' + current.accessToken },
      });
      assertCurrent();
      const email = isRecord(user) ? stringValue(user.email) : null;
      if (!userResponse.ok || !isRecord(user) || uuidValue(user.id) !== scope.userId || email === null) {
        throw authFailure('password-change-user', userResponse.status);
      }

      // The provider's current_password option can be disabled. A password grant
      // verifies the supplied current password without changing provider settings.
      temporarySessionClosed = false;
      const { response, body: temporaryBody } = await this.passwordRequest('/token?grant_type=password', {
        method: 'POST', headers: this.authHeaders(),
        body: JSON.stringify({ email, password: input.currentPassword }),
      });
      // Record any returned token before rejecting a mismatched/late identity,
      // so the temporary session still receives an explicit local logout attempt.
      temporaryAccessToken = isRecord(temporaryBody) ? stringValue(temporaryBody.access_token) : null;
      if (response.status >= 400 && response.status < 500 && temporaryAccessToken === null) {
        // A definite rejected grant created no known session; transport/5xx and
        // malformed successful responses remain uncertain, without a retry.
        temporarySessionClosed = true;
      }
      temporary = await this.readAuthResponse(response, 'password-change-reauthentication', temporaryBody);
      assertCurrent();
      if (temporary.userId !== scope.userId || temporary.expiresAt <= this.now()) {
        throw authFailure('password-change-reauthentication');
      }
      const { response: verified, body: verifiedUser } = await this.passwordRequest('/user', {
        method: 'GET',
        headers: { ...this.authHeaders(), authorization: 'Bearer ' + temporary.accessToken },
      });
      assertCurrent();
      if (!verified.ok || !isRecord(verifiedUser) || uuidValue(verifiedUser.id) !== scope.userId) {
        throw authFailure('password-change-reauthentication', verified.status);
      }

      // No await between the final context check and dispatch.
      assertCurrent();
      if (temporary.expiresAt <= this.now()) throw authFailure('password-change-reauthentication');
      outcome = 'unconfirmed';
      const { response: updated, body: updatedUser } = await this.passwordRequest('/user', {
        method: 'PUT',
        headers: { ...this.authHeaders(), authorization: 'Bearer ' + temporary.accessToken },
        body: JSON.stringify({ password: input.newPassword, current_password: input.currentPassword }),
      });
      if (!updated.ok) {
        if (updated.status >= 400 && updated.status < 500) {
          outcome = 'rejected';
          throw authFailure('password-change-rejected', updated.status);
        }
        throw authFailure('password-change-unconfirmed', updated.status);
      }
      if (!isRecord(updatedUser) || uuidValue(updatedUser.id) !== scope.userId) {
        throw authFailure('password-change-unconfirmed', updated.status);
      }
      outcome = 'changed';
      assertCurrent();
    } catch (error) {
      failure = error instanceof CrestAuthFailure
        ? error
        : authFailure(outcome === 'unconfirmed'
          ? 'password-change-unconfirmed' : 'password-change-reauthentication');
    } finally {
      // Never use global logout here, and never hide the password outcome behind
      // a cleanup failure. No password/session body is retained in diagnostics.
      if (temporaryAccessToken !== null) {
        try {
          const { response: closed } = await this.passwordRequest('/logout?scope=local', {
            method: 'POST',
            headers: { ...this.authHeaders(), authorization: 'Bearer ' + temporaryAccessToken },
          });
          temporarySessionClosed = closed.ok;
        } catch {
          temporarySessionClosed = false;
        }
      }
      this.passwordChangePending = false;
    }

    // A PUT can complete while the user leaves. Never clear a replacement session
    // or present that old operation as the replacement context's success.
    const originCurrent = this.passwordOriginIsCurrent(scope, startingGeneration);
    const stillCurrent = originCurrent && this.passwordScopeIsCurrent(scope);
    if ((outcome === 'changed' || outcome === 'unconfirmed') && originCurrent) this.clearSession();
    if (failure !== null) {
      throw new CrestPasswordChangeFailure(failure.stage, outcome, temporarySessionClosed, failure.status);
    }
    if (!stillCurrent) {
      throw new CrestPasswordChangeFailure('password-change-session-changed', outcome, temporarySessionClosed);
    }
    return { temporarySessionClosed };
  }

  private parseStoredSession(rawSession: string): unknown {
    try {
      return JSON.parse(rawSession);
    } catch {
      return null;
    }
  }
}
